import { analytics } from '@shared/lib/analytics'
import { consentStorage, type ConsentDecision } from '@shared/lib/storage'
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

interface ConsentState {
  decision: ConsentDecision | null
  accept: () => void
  dismiss: () => void
}

const ConsentContext = createContext<ConsentState | undefined>(undefined)

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [decision, setDecision] = useState<ConsentDecision | null>(() =>
    consentStorage.getConsent()
  )

  // Reassert the persisted preference once on mount; the actions handle later decisions.
  useEffect(() => {
    if (decision === 'granted') {
      analytics.optIn()
    } else if (decision === 'denied' || decision === 'dismissed') {
      analytics.optOut()
    }
  }, [])

  const accept = () => {
    consentStorage.setConsent('granted')
    analytics.optIn()
    analytics.trackPageView(window.location.pathname)
    analytics.recordConsentDecision('granted')
    setDecision('granted')
  }

  const dismiss = () => {
    consentStorage.setConsent('dismissed')
    analytics.recordConsentDecision('dismissed')
    analytics.optOut()
    setDecision('dismissed')
  }

  return (
    <ConsentContext.Provider value={{ decision, accept, dismiss }}>
      {children}
    </ConsentContext.Provider>
  )
}

export function useConsent() {
  const context = useContext(ConsentContext)
  if (!context) {
    throw new Error('useConsent must be used within ConsentProvider')
  }
  return context
}
